import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Box, Stack, Select, MenuItem as MuiMenuItem } from "@mui/material";

import CachedIcon from "@mui/icons-material/Cached";
import { AddCircleOutline as AddCircleOutlineIcon } from "@mui/icons-material";

import UserModal from "./component/M_User";
import UserTable from "./component/T_User";

import useAuth from "../../../hooks/useAuth";
import useUser from "../../../hooks/super/useUser";
import { CollapseAlert } from "../../../components/pages/common/styled";
import { UserType } from "../../../utils/constants";
import PageHeader from "../../../components/pages/_new/super/common/PageHeader";
import PrimaryButton from "../../../components/pages/_new/super/common/PrimaryButton";
import OutlinedButton from "../../../components/pages/_new/super/common/OutlinedButton";
import colors from "../../../utils/colors";

function SAUser() {
  const navigate = useNavigate();
  const { orgID } = useParams();
  const { isAuthenticated, adminUserType } = useAuth();
  const { organisations, getOrganisations, getUsers, users, setErr, errMsg } = useUser();
  const [errOpen, setErrOpen] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const selectOrgID = (event) => {
    setErr(null);
    getUsers(event.target.value);
    navigate("/super/application/user/list/" + event.target.value);
  };
  const refresh = () => {
    getUsers(orgID);
  };
  React.useEffect(() => {
    window.scrollTo(0, 0);
    if (isAuthenticated) {
      getOrganisations();
      if (orgID) {
        getUsers(orgID);
      }
    }
    return () => setErr(null);
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // If the route landed without an :orgID (e.g. sidebar -> /super/application/user/list),
  // auto-select the first organisation once the list resolves so the page never shows
  // "Organisation undefined". When the URL already carries an :orgID, honour it.
  React.useEffect(() => {
    if (!isAuthenticated) return;
    if (orgID) return;
    if (!Array.isArray(organisations) || organisations.length === 0) return;
    const firstId = organisations[0]?.id;
    if (!firstId) return;
    navigate("/super/application/user/list/" + firstId, { replace: true });
    getUsers(firstId);
  }, [isAuthenticated, orgID, organisations]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    setErrOpen(true);
  }, [errMsg]);

  return (
    <React.Fragment>
      <Helmet title="SA User" />
      <PageHeader
        title="User Management"
        description="Manage users within organisations"
        actions={
          <Stack direction="row" spacing={3} alignItems="center">
            <Select
              value={orgID || ""}
              onChange={selectOrgID}
              displayEmpty
              sx={{
                width: "280px",
                height: 36,
                borderRadius: "8px",
                fontSize: 14,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.neutral[300],
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.primary[300],
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.primary[300],
                },
              }}
            >
              {(!organisations || organisations.length === 0) && (
                <MuiMenuItem value="" disabled sx={ { fontSize: 14 }}>
                  {organisations === null ? "Loading organisations..." : "No organisations available"}
                </MuiMenuItem>
              )}
              {organisations?.map((org, i) => {
                return (
                  <MuiMenuItem key={i} value={org.id} sx={ { fontSize: 14 }}>
                    {org.title}
                  </MuiMenuItem>
                );
              })}
            </Select>
            <OutlinedButton
              onClick={refresh}
              startIcon={<CachedIcon style={{ width: 20, height: 20 }} />}
            >
              Refresh
            </OutlinedButton>
            <PrimaryButton
              onClick={handleOpen}
              startIcon={<AddCircleOutlineIcon style={{ width: 20, height: 20 }} />}
              disabled={null === users || ![UserType.SUPER_ADMIN, UserType.ADMIN].includes(adminUserType)}
            >
              Add New User
            </PrimaryButton>
          </Stack>
        }
      />
      <Box sx={{ mb: 4 }}>
        <CollapseAlert errOpen={errOpen} setErrOpen={setErrOpen} setErr={setErr} errMsg={errMsg} />
      </Box>
      <Stack spacing={4}>
        <UserTable />
      </Stack>
      <UserModal open={open} handleClose={handleClose} />
    </React.Fragment>
  );
}
export default SAUser;
